import { nothing } from 'lit-html';
import { html, internalProperty, property } from 'lit-element';
import { ifNotNull } from '../../utils/directives';
import { checkSlots, cleanDefaultSlot } from '../../shared/slot-utils';
import {
  KatLitElement,
  Keys,
  register,
  event,
  EventEmitter,
} from '../../shared/base';
import { nextUniqueId } from '../../shared/base/unique-id';
import {
  getFirstMatchingParent,
  getContentOfFirstTextNode,
} from '../../shared/utils';
import baseStyles from '../../shared/base/base.lit.scss';
import formItemStyles from '../../shared/base/form-item.base.lit.scss';
import { debounce } from '../../utils/debounce';
import styles from './dropdown.lit.scss';
import { fitHandler } from './fit-handler';
import { strings } from './strings';

/**
 * @component {kat-dropdown} KatalDropdown A dropdown is a form element that allows the selection of a single choice from a list of items.
 * @subcomponent ./option.ts
 * @guideline Do Stringify data object when passing it to attribute data
 * @guideline Do If possible, pre-select a good default item from the list to help the customer make an informed decision——similar to having a good default input value in an input field.
 * @guideline Do If possible, only include valid options in a dropdown. A user should typically not be able to choose an option that is invalid.
 * @guideline Dont Do not use the descriptions under the labels for each item unless absolutely necessary.
 * @slot default Can be used instead of the `options` property for more control over the options' appearance. Should be filled with `kat-option` elements.
 * @example Basic {"label": "Basic Dropdown", "value":"testValue2", "options": [{"name":"Value1","value":"testValue","icon":"optional-icon-name"},{"name":"Value2","value":"testValue2","icon":"optional-icon-name-2"},{"name":"disabledValue","value":"disabledValue","icon":"optional-icon-name", "disabled": true}]}
 * @example Large {"value":"", "options": [{"name":"car","value":"carValue"},{"name":"truck","value":"truckValue"},{"name":"house","value":"houseValue"},{"name":"plane","value":"planeValue"},{"name":"cat","value":"catValue"},{"name":"dog","value":"dogValue"},{"name":"fish","value":"fishValue"},{"name":"bird","value":"birdValue"},{"name":"lizard","value":"lizardValue"},{"name":"turtle","value":"turtleValue"},{"name":"bug","value":"bugValue"}]}
 * @example CountriesAsSlots {"rich-selection-label":"true", "constraint-emphasis":"Note:", "constraint-label":"When using slots to provide options, the styling is completely up to you. The Dropdown component will not define or restrain styles for you.", "value": "", "content": "
 *   <style>
 *       .flexed {
 *         display: flex;
 *         align-items: center;
 *       }
 *
 *       .padded {
 *         margin-left: 8px;
 *       }
 *     </style>
 *   <kat-option value=\"ca\">
 *     <div class=\"flexed\">
 *        <kat-icon name=\"flag-ca\" size=\"small\"></kat-icon>
 *         <span class=\"padded\">Canada</span>
 *       </div>
 *     </kat-option>
 *     <kat-option value=\"de\">
 *       <div class=\"flexed\">
 *         <kat-icon name=\"flag-de\" size=\"small\"></kat-icon>
 *         <span class=\"padded\">Germany</span>
 *       </div>
 *     </kat-option>
 *     <kat-option value=\"it\">
 *       <div class=\"flexed\">
 *         <kat-icon name=\"flag-it\" size=\"small\"></kat-icon>
 *         <span class=\"padded\">Italy</span>
 *       </div>
 *     </kat-option>
 *     <kat-option value=\"us\">
 *       <div class=\"flexed\">
 *         <kat-icon name=\"flag-us\" size=\"small\"></kat-icon>
 *         <span class=\"padded\">United States</span>
 *       </div>
 *     </kat-option>
 * "}
 * @example CountriesAsOptions {"rich-selection-label":"true", "placeholder":"Countries", "options": [{"name":"USA","value":"us","icon":"flag-us"},{"name":"Canada","value":"ca","icon":"flag-ca"},{"name":"Mexico","value":"mx","icon":"flag-mx"},{"name":"New Zealand","value":"nz"},{"name":"UAE","value":"uae" }]}
 * @example WithIcons {"options": [{"name":"info","value":"info","icon":"info"},{"name":"input","value":"input","icon":"input"},{"name":"inbox","value":"inbox","icon":"inbox"}]}
 * @example Error {"value": "", "placeholder": "Pick a topping", "state": "error", "options": [{"name":"Pepperoni","value":"pepperoni"},{"name":"Onions","value":"onions"},{"name":"Pineapple","value":"pineapple"}]}
 * @example WithLabels {"value": "", "placeholder": "Pick a topping", "state-label": "Error with selection",
 * "state-emphasis": "Error", "label": "Dropdown with labels", "constraint-emphasis": "Note", "constraint-label": "This is a constraint label",
 * "options": [{"name":"Pepperoni","value":"pepperoni"},{"name":"Onions","value":"onions"},{"name":"Pineapple","value":"pineapple"}]}
 * @example WithTooltip {"label": "Dropdown with tooltip", "tooltip-text": "Hello world",
 * "options": [{"name":"Pepperoni","value":"pepperoni"},{"name":"Onions","value":"onions"},{"name":"Pineapple","value":"pineapple"}]}
 * @status Production
 * @theme flo
 */
@register('kat-dropdown')
export class KatDropdown extends KatLitElement {
  /** Array of option objects providing the name, value, and optional icon. Each array element represents a single dropdown option. */
  @property()
  options: { name: string; value: string; icon?: string }[] = [];

  /** The text that will be displayed to the user as a placeholder. */
  @property()
  placeholder?: string;

  /**
   * The max height of the selection area.
   * This should be a css value with units, e.g. "8px".
   */
  @property({ attribute: 'max-height' })
  maxHeight?: string;

  /** The optional field name, hidden from view. */
  @property()
  name?: string;

  /** Whether the component is currently showing its dropdown options. */
  @property()
  expanded?: boolean;

  /** The value of the selected option. */
  @property()
  value?: string;

  /**
   * If set to `true` the selection value will be shown as it appears in the selection option.
   * Otherwise only selection value will be shown as simple text
   * Defaults to `false`
   */
  @property({ attribute: 'rich-selection-label' })
  richSelectionLabel? = false;

  /**
   * The size of the selected option.
   * @enum {value} large Large select box - Default
   * @enum {value} small Small select box
   */
  @property()
  size: 'large' | 'small' = 'large';

  /** Setting the select box to disabled will stop its interactivity. */
  @property()
  disabled?: boolean;

  /** The label of the dropdown visible only to screenreaders. */
  @property({ attribute: 'kat-aria-label' })
  katAriaLabel?: string;

  /** The optional field label. */
  @property()
  label?: string;

  /**
   * Optional pretext for the constraint label.  Used to
   * provide addtional context to the constraint for the input
   */
  @property({ attribute: 'constraint-emphasis' })
  constraintEmphasis?: string;

  /** Provides users with more information about what they enter into the input */
  @property({ attribute: 'constraint-label' })
  constraintLabel?: string;

  /**
   * Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
   * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
   */
  @property()
  state?: string;

  /** Optional pretext for the state label. Provides additional context to the state for the input. */
  @property({ attribute: 'state-emphasis' })
  stateEmphasis?: string;

  /** Provides users with more information about why the input is in the state it is in */
  @property({ attribute: 'state-label' })
  stateLabel?: string;

  /**
   * The text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
   * set.
   */
  @property({ attribute: 'tooltip-text' })
  tooltipText?: string;

  /** The position of the tooltip. Defaults to "top". */
  @property({ attribute: 'tooltip-position' })
  tooltipPosition?: string;

  /** The icon that triggers the tooltip next to the label. Defaults to "help_outline". */
  @property({ attribute: 'tooltip-trigger-icon' })
  tooltipTriggerIcon?: string;

  /** Setting the select box to searchable will add a filter box to refine the options displayed */
  @property()
  searchable?: boolean;

  // Needs to be in the property list so that changing it will re-render the component
  @internalProperty()
  _searchTerm: string;

  /** Locale for the "no options" text in search bar */
  @property()
  locale = 'en-US';

  static get styles() {
    return [baseStyles, formItemStyles, styles];
  }

  _propsToReflectToHiddenInput = ['name', 'value', 'disabled'];

  /** Fired after the dropdown value has changed. */
  @event('change', true)
  private _change: EventEmitter<{ value: string }>;

  /** Fired whenever the dropdown is expanded. */
  @event('expand')
  private _expand: EventEmitter;

  /** Fired whenever the dropdown is collapsed. */
  @event('collapse')
  private _collapse: EventEmitter;

  private _jumpToSearch = '';
  private _timeoutId = 0;
  private _uniqueId = nextUniqueId();
  // Trigger for "no options" message
  private _allOptionsAreHidden = false;

  constructor() {
    super();

    cleanDefaultSlot(this);
    this.observeChildren(this, () => this.requestUpdate());
  }

  firstUpdated() {
    this.createEvents();
  }

  onInput(e) {
    this.onInputDebounced(e.target.value);
  }

  // Only used for searchable drop-downs; this responds to the input field
  @debounce(150)
  onInputDebounced(searchTerm: string) {
    this._searchTerm = searchTerm;

    if (this._hasSlottedOptions()) {
      this.toggleElementVisibility();
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('expanded')) {
      this.setOrUnsetOptionsContainerBottomOffset();
    }

    if (changedProperties.has('value')) {
      const options = Array.from(
        this.querySelectorAll('kat-option:not([slot])')
      );
      options.forEach(opt => {
        const option: any = opt;
        if (option.selected) option.selected = false;
        if (option.value === this.value) option.selected = true;
      });

      if (this.richSelectionLabel) {
        this._setSelectionAsRichLabel(options);
      }
    }
  }

  private _setSelectionAsRichLabel(options: any[]) {
    let selectedOption = (options.length > 0
      ? options.find(opt => (opt as any).selected === true)
      : this.shadowRoot
          .querySelector('.option-inner-container')
          .querySelector('kat-option[selected]')) as HTMLElement;

    if (selectedOption.firstElementChild) {
      selectedOption = selectedOption.firstElementChild.cloneNode(
        true
      ) as HTMLElement;
    } else {
      const span = document.createElement('span');
      span.innerText = selectedOption.innerText;
      selectedOption = span;
    }
    selectedOption.setAttribute('slot', 'placeholder');

    const slotContent = this.querySelector('[slot=placeholder]');
    if (slotContent) {
      this.removeChild(slotContent);
    }
    this.appendChild(selectedOption);
  }

  renderOptions() {
    const theSearchTerm = this._searchTerm;
    const isSearchable = this.searchable;
    const searchable = isSearchable && theSearchTerm;
    let hasResult = false;
    const ret = this.options.map((option, index) => {
      const { name, value, icon, disabled } = option;
      if (
        searchable &&
        !name.toLowerCase().includes(theSearchTerm.toLowerCase())
      ) {
        return html`${nothing}`;
      }
      hasResult = true;
      const selected = value === this.value ? 'selected' : '';

      const iconElement = icon
        ? html`<kat-icon name="${icon}" size="tiny"></kat-icon>`
        : nothing;
      return html`
        <kat-option
          part="dropdown-option${index}"
          ?selected="${selected}"
          tabindex="-1"
          value="${value}"
          ?disabled=${disabled}
        >
          <div class="standard-option-content">
            <div class="standard-option-name">${name}</div>
            <div class="standard-option-icon">${iconElement}</div>
          </div>
        </kat-option>
      `;
    });

    this._allOptionsAreHidden = searchable && !hasResult;
    return ret;
  }

  renderNoOptionsMsg() {
    // This text needs to be localized
    return html`
      <kat-option disabled="true">
        <div class="standard-option-content">
          <div class="standard-option-name">
            ${this.getString('no_options')}
          </div>
        </div>
      </div>
    `;
  }

  renderSearchBar() {
    if (this.expanded) {
      return html`
        <div class="search-wrapper">
          <input
            class="search-input"
            @input=${this.onInput}
            @keydown=${this.searchBarKeydown}
          />
          <div class="search-icon">
            <kat-icon name="search" size="small"></kat-icon>
          </div>
        </div>
      `;
    }
    return undefined;
  }

  // Only for slots, and only if the list is searchable
  toggleElementVisibility() {
    let hasVisibleOptions = false;
    const elts = this.getElementsByTagName('kat-option');
    for (let eltIdx = 0; eltIdx < elts.length; eltIdx++) {
      const searchkey = elts[eltIdx].searchkey;
      const textContent = elts[eltIdx].textContent;
      if (
        this._searchTerm &&
        ((!searchkey && !this.matchesSearchTerm(textContent)) ||
          (searchkey && !this.matchesSearchTerm(searchkey)))
      ) {
        elts[eltIdx].classList.add('search-term-hidden');
      } else {
        elts[eltIdx].classList.remove('search-term-hidden');
        hasVisibleOptions = true;
      }
    }

    this._allOptionsAreHidden = !hasVisibleOptions;
  }

  private matchesSearchTerm(optionText: string) {
    return optionText.toLowerCase().includes(this._searchTerm.toLowerCase());
  }

  render() {
    let displayTextClass = 'header-row-text ';

    let displayText = '';
    if (this.value) {
      if (this._hasSlottedOptions()) {
        const matching = Array.from(this.children).find(
          child => child.tagName === 'KAT-OPTION' && child.value === this.value
        );
        if (matching) {
          displayText = getContentOfFirstTextNode(matching);
        }
      } else if (this.options) {
        const value = this.options.find(object => object.value === this.value);
        if (value) {
          displayText = value.name;
        }
      }
    }

    if (!displayText && this.placeholder) {
      displayText = this.placeholder;
      displayTextClass += 'placeholder ';
    } else {
      displayTextClass += 'value ';
    }

    const formLabel = this.label
      ? html`
          <kat-label
            unique-id="form-label"
            class="form-label"
            part="dropdown-label"
            text="${this.label}"
            for=${this._uniqueId}
            .tooltipText=${ifNotNull(this.tooltipText)}
            .tooltipPosition=${ifNotNull(this.tooltipPosition)}
            .tooltipTriggerIcon=${ifNotNull(this.tooltipTriggerIcon)}
          ></kat-label>
        `
      : nothing;

    const constraintLabel = this.constraintLabel
      ? html`
          <kat-label
            class="constraint-label"
            variant="constraint"
            emphasis="${ifNotNull(this.constraintEmphasis)}"
            text="${this.constraintLabel}"
            part="dropdown-constraint-label"
          ></kat-label>
        `
      : nothing;

    const stateLabel = this.stateLabel
      ? html`
          <kat-label
            class="state-label"
            variant="constraint"
            text="${this.stateLabel}"
            emphasis="${ifNotNull(this.stateEmphasis)}"
            state="${this.state}"
            part="dropdown-state-label"
          ></kat-label>
        `
      : nothing;

    const searchBar = this.searchable ? this.renderSearchBar() : nothing;

    // `nothing` is truthy so if we don't have JSON defined options we need to render an empty default
    // slot for the slotted content.
    const options = this._hasSlottedOptions() ? nothing : this.renderOptions();

    // We only want to render the default slot if there is either fallback content to render (JSON
    // case) or slotted content. As there is a bug with rendering empty fallback content in
    // polyfilled browsers.
    const defaultSlot = options ? html`<slot>${options}</slot>` : nothing;

    const noOptionMsg = this._allOptionsAreHidden
      ? this.renderNoOptionsMsg()
      : nothing;

    return html`
      ${formLabel}
      <div
        class="kat-select-container"
        aria-labelledby="form-label"
        aria-label=${ifNotNull(this.katAriaLabel)}
        role="listbox"
      >
        <div
          id=${this._uniqueId}
          class="select-header"
          part="dropdown-header"
          tabindex=${ifNotNull(this.disabled ? null : '0')}
          @click=${this.headerClick}
          @keydown=${this.headerKeydown}
        >
          <div class="header-row">
            <div class="${displayTextClass}">
              <slot name="placeholder">${displayText}</slot>
            </div>
            <div class="indicator">
              <kat-icon name="chevron-down" size="small"></kat-icon>
            </div>
          </div>
        </div>
        <span class="ring"></span>
        <div class="select-options" part="dropdown-options">
          ${searchBar}
          <div
            class="option-inner-container"
            style="${ifNotNull(
              this.maxHeight
                ? 'max-height:' + this.maxHeight + ';overflow:auto;'
                : null
            )}"
            @selected=${this.selectOptionClick}
            @click=${this.selectOptionClick}
            @keydown=${this.selectOptionKeydown}
          >
            ${defaultSlot} ${noOptionMsg}
          </div>
        </div>
      </div>
      <div class="metadata">${constraintLabel} ${stateLabel}</div>
      <slot name="private-light-dom"></slot>
    `;
  }

  createEvents() {
    this.addEventListener('focusout', e => {
      const container = this.shadowRoot.querySelector('.kat-select-container');
      const newFocus = e.relatedTarget;
      if (
        !this.disabled &&
        !container.contains(newFocus) &&
        !this.contains(newFocus)
      ) {
        this.collapse();
      }
    });
  }

  async searchOptions(autoselect) {
    const search = this._jumpToSearch.toLowerCase();
    const options = this._getOptions();
    const matching = options.find(opt => {
      const text = getContentOfFirstTextNode(opt);
      return text?.toLowerCase().startsWith(search) && !opt.disabled;
    });
    if (matching) {
      if (autoselect && this.expanded !== true) {
        await this.selectOption(matching);
      } else {
        if (!this.expanded) {
          this.expanded = true;
          await this.updateComplete;
        }
        matching.focus();
      }
    }
  }

  async selectOption(element) {
    if (element.tagName !== 'KAT-OPTION') return;
    if (element.disabled) return;

    const initialValue = this.value;
    const newValue = element.value;
    if (initialValue !== newValue) {
      this.value = newValue;
      await this.updateComplete;
      this._change.emit({ value: this.value });
    }

    this.collapseAndFocus();
  }

  collapseAndFocus() {
    this.focus();
    this.collapse();
  }

  async collapse() {
    this.expanded = false;
    await this.updateComplete;
    this._collapse.emit();

    // Next time it's opened, all items should be displayed
    if (this.searchable) {
      this._searchTerm = '';
    }
  }

  focusSearchBar() {
    // Automatically jump to input field
    const theInput = this.shadowRoot.querySelector('input');
    theInput.focus();
  }

  async toggleExpanded() {
    if (this.expanded) {
      this.collapse();
    } else {
      this.expanded = true;
      await this.updateComplete;
      if (this.searchable) {
        this.focusSearchBar();
      }
      this._expand.emit();
    }
  }

  fitDropDownWindow() {
    const optionContainer = this.shadowRoot.querySelector('.select-options');
    const optionInnerContainer = optionContainer.querySelector(
      '.option-inner-container'
    );

    optionContainer.style.display = 'table';
    fitHandler(this, optionInnerContainer, !this.searchable);
    optionContainer.style.removeProperty('display');
  }

  setOrUnsetOptionsContainerBottomOffset() {
    const selectOptionsElement = this.shadowRoot.querySelector(
      '.select-options'
    );

    if (!selectOptionsElement) {
      return;
    }

    this.fitDropDownWindow();

    if (this.hasAttribute('drop-up')) {
      const offset = this.shadowRoot.querySelector('div.select-header')
        .offsetHeight;

      selectOptionsElement.setAttribute('style', `bottom: ${offset}px`);
    } else {
      selectOptionsElement.removeAttribute('style');
    }
  }

  selectOptionClick(event) {
    if (this.disabled) {
      return;
    }

    const el = getFirstMatchingParent(event.target, el =>
      el.hasAttribute('value')
    );
    if (el && el !== this) {
      this.selectOption(el);
    }
  }

  searchBarKeydown(event) {
    const keyCode = event.keyCode || event.which;

    if (keyCode === Keys.ArrowDown) {
      event.preventDefault();
      this.focusInitialItem();
    } else if (keyCode === Keys.ArrowUp) {
      event.preventDefault();
      this.collapseAndFocus();
    }
  }

  selectOptionKeydown(event) {
    const target = event.target;
    const keyCode = event.keyCode || event.which;

    if (!this.disabled && target) {
      if (
        (keyCode === Keys.Space && !this._jumpToSearch) ||
        keyCode === Keys.Enter
      ) {
        event.preventDefault();
        this.selectOption(target);
      } else if (keyCode === Keys.ArrowUp) {
        //previous
        event.preventDefault();
        let prevEl = target.previousElementSibling;

        while (prevEl) {
          if (!prevEl.disabled) {
            prevEl.focus();
            return;
          }
          prevEl = prevEl.previousElementSibling;
        }
        // At the top of the list
        if (this.searchable) {
          this.focusSearchBar();
        } else {
          this.collapseAndFocus();
        }
      } else if (keyCode === Keys.ArrowDown) {
        //next
        event.preventDefault();
        let nextEl = target.nextElementSibling;
        while (nextEl) {
          if (!nextEl.disabled) {
            nextEl.focus();
            return;
          }
          nextEl = nextEl.nextElementSibling;
        }
      } else if (keyCode === Keys.Tab) {
        // Do nothing, let the browser do what it does best!
      } else if (keyCode === Keys.Escape) {
        this.collapseAndFocus();
      } else {
        this.jumpToSearch(keyCode, true);
      }
    }
  }

  // Select item in a newly-opened header
  focusInitialItem() {
    const options = this._getOptions();
    let selectedOption = options.find(element => element.selected);
    if (!selectedOption) {
      selectedOption = options.find(element => !element.disabled);
    }
    if (selectedOption) {
      selectedOption.focus();
    }
  }

  async headerKeydown(event) {
    const keyCode = event.keyCode || event.which;
    if (
      keyCode === Keys.ArrowDown ||
      keyCode === Keys.Enter ||
      (keyCode === Keys.Space && !this._jumpToSearch)
    ) {
      event.preventDefault();
      this.expanded = true;
      await this.updateComplete;
      if (this.searchable) {
        this.focusSearchBar();
      } else {
        this.focusInitialItem();
      }
    } else {
      this.jumpToSearch(keyCode, true);
    }
  }

  headerClick(e) {
    const container = this.shadowRoot.querySelector('.kat-select-container');
    const newFocus = e.relatedTarget;
    if (!this.disabled && !container.contains(newFocus)) {
      this.toggleExpanded();
    }
  }

  async jumpToSearch(keyCode, autoselect = false) {
    this._jumpToSearch += String.fromCharCode(keyCode);

    clearTimeout(this._timeoutId);
    await this.searchOptions(autoselect);
    this._timeoutId = setTimeout(() => (this._jumpToSearch = ''), 300);
  }

  /**
   * Programmatically focus the header.
   * @katalmethod
   */
  focus() {
    const header = this.shadowRoot.querySelector('.select-header');
    if (header) {
      header.focus();
    }
  }

  _hasSlottedOptions() {
    return !!checkSlots(this).default;
  }

  _getOptions() {
    if (this._hasSlottedOptions()) {
      return Array.from(this.getElementsByTagName('kat-option'));
    }

    return Array.from(
      this.shadowRoot.querySelectorAll('.option-inner-container kat-option')
    );
  }

  // Pulled this directly from HMD- should be a utility function
  getString(stringId) {
    const locale = this.locale;
    let string = strings[stringId][locale];
    if (!string) {
      string = strings[stringId]['en-US'];
    }
    return string;
  }
}
